import { Rng } from '../core/rng';
import {
  type Chord,
  chooseMelodyNote,
  lowestOf,
  melodyPcs,
  nearestInSet,
  notesInRange,
  parseRoman,
  pc,
  scalePcs,
  stepInScale,
  voiceLead,
} from './theory';
import {
  DEFAULT_RHYTHMS,
  type InstrumentId,
  type Layer,
  LEITMOTIF,
  type PartDef,
  type ThemeDef,
} from './themes';

/**
 * Deterministic generative sequencer. Given a theme and a seed it produces one
 * bar at a time: harmony from the theme's progressions, and notes for every
 * part (voice-led chords, arpeggios, bass lines, phrase-structured melodies
 * with call/response and the leitmotif, ostinati, drum patterns with fills).
 * Times are in beats; the music engine converts them to seconds.
 *
 * Form: sections of `sectionBars` bars cycle through four sections (parts can
 * be limited to some of them so the texture keeps changing); inside a section
 * melodies are built from 4-bar phrases: call, answer, sequenced call, cadence.
 */

export interface NoteEvent {
  beat: number;
  dur: number;
  midi: number;
  inst: InstrumentId;
  vel: number;
  layer: Layer;
  /** Index of the part in the theme. */
  part: number;
}

export interface BarPlan {
  index: number;
  beats: number;
  /** Section of the 4-section cycle. */
  section: number;
  barInSection: number;
  chord: Chord;
  chordStart: boolean;
  events: NoteEvent[];
}

interface MelodyNote {
  beat: number;
  dur: number;
  midi: number;
  strong: boolean;
}

interface PartState {
  voicing: number[] | null;
  prev: number;
  lastInterval: number;
  call: MelodyNote[] | null;
}

const DEFAULT_INTENSITY: readonly [number, number, number, number] = [0.85, 1, 1, 0.85];

/** Shifts a note by octaves into [lo, hi] when the range allows it. */
export function fold(midi: number, lo: number, hi: number): number {
  let n = midi;
  if (hi - lo < 11) return Math.min(hi, Math.max(lo, n));
  while (n < lo) n += 12;
  while (n > hi) n -= 12;
  return n;
}

/** Smallest note above `root` (within an octave) with pitch class `p`. */
const above = (root: number, p: number): number => root + (((p - pc(root) + 11) % 12) + 1);

export class ThemeSequencer {
  readonly tonicPc: number;
  readonly scale: number[];
  readonly sectionBars: number;
  readonly feel: '44' | '34' | '68';
  private readonly rng: Rng;
  private readonly chords: Chord[][];
  private readonly states: PartState[];
  private bar = 0;

  constructor(
    readonly theme: ThemeDef,
    seed: number | string = 1,
  ) {
    this.rng = new Rng(seed);
    this.tonicPc = pc(theme.tonic);
    this.scale = scalePcs(this.tonicPc, theme.scale);
    this.sectionBars = Math.max(1, theme.sectionBars ?? 8);
    this.feel = theme.feel ?? (theme.beatsPerBar === 3 ? '34' : '44');
    this.chords = theme.progressions.map((p) => p.map((s) => parseRoman(s, this.tonicPc)));
    if (!this.chords.length || this.chords.some((p) => !p.length))
      throw new Error(`Theme ${theme.id} has an empty progression`);
    this.states = theme.parts.map((p) => {
      const [lo, hi] = p.range ?? [60, 72];
      return {
        voicing: null,
        prev: nearestInSet((lo + hi) / 2, [this.tonicPc], lo, hi),
        lastInterval: 0,
        call: null,
      };
    });
  }

  /** Bars generated so far. */
  get barIndex(): number {
    return this.bar;
  }

  /** Form position and harmony of a bar (a pure function of the bar index). */
  position(bar: number): {
    section: number;
    barInSection: number;
    chord: Chord;
    chordStart: boolean;
  } {
    const sectionCount = Math.floor(bar / this.sectionBars);
    const barInSection = bar - sectionCount * this.sectionBars;
    // Rotate which progression meets which section every full cycle for variety.
    const prog = this.chords[(sectionCount + Math.floor(sectionCount / 4)) % this.chords.length]!;
    const bpc = Math.max(1, this.theme.barsPerChord);
    const ci = Math.floor(barInSection / bpc) % prog.length;
    return {
      section: sectionCount % 4,
      barInSection,
      chord: prog[ci]!,
      chordStart: barInSection % bpc === 0,
    };
  }

  nextBar(): BarPlan {
    const index = this.bar++;
    const pos = this.position(index);
    const plan: BarPlan = { index, beats: this.theme.beatsPerBar, ...pos, events: [] };
    const inten = (this.theme.intensity ?? DEFAULT_INTENSITY)[pos.section] ?? 1;
    this.theme.parts.forEach((part, i) => {
      if (part.sections && !part.sections.includes(pos.section)) return;
      if (part.bars && !part.bars.includes(pos.barInSection)) return;
      this.generate(part, i, plan, inten);
    });
    plan.events.sort((a, b) => a.beat - b.beat || a.part - b.part);
    return plan;
  }

  // ------------------------------------------------------------------ helpers

  private vary(): number {
    return 0.9 + this.rng.next() * 0.2;
  }

  /** Beats that carry the metre's pulse (accents). */
  private isPulse(beat: number): boolean {
    const unit = this.feel === '68' ? 1.5 : 1;
    return Math.abs(beat / unit - Math.round(beat / unit)) < 1e-6;
  }

  /** Beats where melodies land on chord tones. */
  private isStrong(beat: number): boolean {
    const unit = this.feel === '44' ? 2 : this.feel === '68' ? 1.5 : 3;
    return Math.abs(beat / unit - Math.round(beat / unit)) < 1e-6;
  }

  private generate(part: PartDef, i: number, plan: BarPlan, inten: number): void {
    const st = this.states[i]!;
    const [lo, hi] = part.range ?? [48, 72];
    const beats = plan.beats;
    const layer = part.layer ?? 'base';
    const baseVel = (part.vel ?? 0.6) * inten;
    const chord = plan.chord;
    const push = (beat: number, dur: number, midi: number, vel: number): void => {
      if (beat < -1e-9 || beat >= beats - 1e-9 || !(dur > 0) || !Number.isFinite(midi)) return;
      plan.events.push({
        beat,
        dur,
        midi,
        inst: part.inst,
        vel: Math.min(1, Math.max(0.02, vel)),
        layer,
        part: i,
      });
    };

    switch (part.role) {
      case 'chord': {
        if (!plan.chordStart) return;
        const v = voiceLead(st.voicing, chord.pcs, lo, hi);
        st.voicing = v;
        const dur = beats * Math.max(1, this.theme.barsPerChord) * (part.legato ?? 1);
        for (const n of v) push(0, dur, n, baseVel * this.vary());
        if (part.double) push(0, dur, lowestOf(chord.root, lo - 12, lo - 1), baseVel * 0.8);
        return;
      }
      case 'arp': {
        const rate = part.rate ?? 0.5;
        const tones = notesInRange(chord.pcs, lo, hi).slice(0, 7);
        if (!tones.length) return;
        const m = tones.length;
        let seq: number[];
        switch (part.arp ?? 'up') {
          case 'down':
            seq = [...tones].reverse();
            break;
          case 'updown':
            seq = [...tones, ...tones.slice(1, -1).reverse()];
            break;
          case 'broken':
            seq = [0, 2, 1, 2].map((k) => tones[k % m]!);
            break;
          default:
            seq = tones;
        }
        const steps = Math.round(beats / rate);
        let last = -1;
        for (let k = 0; k < steps; k++) {
          let n: number;
          if (part.arp === 'random') {
            let j = this.rng.int(0, m - 1);
            if (j === last && m > 1) j = (j + 1 + this.rng.int(0, m - 2)) % m;
            last = j;
            n = tones[j]!;
          } else {
            n = seq[k % seq.length]!;
          }
          const beat = k * rate;
          push(beat, rate * 1.8, n, baseVel * (this.isPulse(beat) ? 1 : 0.78) * this.vary());
        }
        return;
      }
      case 'bass': {
        const root = lowestOf(part.bass === 'pedal' ? this.tonicPc : chord.root, lo, hi);
        const fifthPc = chord.pcs[chord.quality === 'pow' ? 1 : 2] ?? (chord.root + 7) % 12;
        let fifth = above(root, fifthPc);
        if (fifth > hi) fifth -= 12;
        const v = baseVel;
        switch (part.bass ?? 'half') {
          case 'whole':
          case 'pedal':
            push(0, beats * 0.98, root, v);
            break;
          case 'half':
            if (beats >= 4) {
              push(0, 1.9, root, v);
              push(2, 1.9, this.rng.next() < 0.5 ? fifth : root, v * 0.85);
            } else {
              push(0, beats - 1.05, root, v);
              push(beats - 1, 0.95, fifth, v * 0.8);
            }
            break;
          case 'waltz':
            push(0, 0.95, root, v);
            break;
          case 'pulse8':
            for (let b = 0; b < beats - 1e-9; b += 0.5) {
              const off = !this.isPulse(b);
              const oct = off && this.rng.next() < 0.22;
              push(b, 0.42, oct ? root + 12 : root, v * (off ? 0.72 : 1));
            }
            break;
          case 'dotted':
            push(0, 1.45, root, v);
            push(1.5, 1.45, this.rng.next() < 0.6 ? fifth : root, v * 0.85);
            break;
          case 'gallop':
            for (let b = 0; b < beats; b++) {
              push(b, 0.45, root, v);
              push(b + 0.5, 0.22, root, v * 0.7);
              push(b + 0.75, 0.22, root, v * 0.75);
            }
            break;
        }
        return;
      }
      case 'melody':
        this.melody(part, st, plan, lo, hi, baseVel, push);
        return;
      case 'ostinato': {
        const pat = part.pattern ?? 'r.r.r.r.r.r.r.r.';
        const step = beats / pat.length;
        const root = lowestOf(chord.root, lo, hi);
        const third = above(root, chord.pcs[1] ?? (chord.root + 4) % 12);
        const fifth = above(
          root,
          chord.pcs[chord.quality === 'pow' ? 1 : 2] ?? (chord.root + 7) % 12,
        );
        for (let k = 0; k < pat.length; k++) {
          const ch = pat[k];
          const n =
            ch === 'r'
              ? root
              : ch === '3'
                ? third
                : ch === '5'
                  ? fifth
                  : ch === '8'
                    ? root + 12
                    : null;
          if (n === null) continue;
          const beat = k * step;
          push(
            beat,
            step * 0.9 * (part.legato ?? 1),
            n,
            baseVel * (this.isPulse(beat) ? 1 : 0.72) * this.vary(),
          );
        }
        return;
      }
      case 'drums': {
        const pat = part.pattern ?? 'X...';
        const step = beats / pat.length;
        const tuned = part.range !== undefined;
        const root = tuned ? lowestOf(chord.root, lo, hi) : 60;
        let fifth = tuned ? above(root, (chord.root + 7) % 12) : 60;
        if (fifth > hi) fifth -= 12;
        const len = Math.max(0.25, step * 0.9);
        const taken = new Set<number>();
        for (let k = 0; k < pat.length; k++) {
          const ch = pat[k];
          const beat = k * step;
          if (ch === 'X' || ch === 'x' || ch === 'o') {
            const mul = ch === 'X' ? 1 : ch === 'x' ? 0.72 : 0.42;
            push(
              beat,
              len,
              ch === 'x' && tuned && k > 0 ? fifth : root,
              baseVel * mul * this.vary(),
            );
            taken.add(Math.round(beat * 4));
          } else if (ch === 'r') {
            for (let r = 0; r < 4; r++)
              push(beat + (r * step) / 4, len / 4, root, baseVel * (0.35 + r * 0.15));
            taken.add(Math.round(beat * 4));
          }
        }
        // Fills at the end of each 4-bar phrase: sixteenths crescendo into the next downbeat.
        if (part.fill && plan.barInSection % 4 === 3 && this.rng.next() < part.fill * inten) {
          for (let b = beats - 1; b < beats - 1e-9; b += 0.25) {
            if (taken.has(Math.round(b * 4))) continue;
            if (this.rng.next() < 0.7)
              push(b, 0.25, root, baseVel * (0.4 + 0.45 * (b - (beats - 1))));
          }
        }
        return;
      }
      case 'stab': {
        const pat = part.pattern ?? 'X...';
        const step = beats / pat.length;
        const v = voiceLead(st.voicing, chord.pcs, lo, hi);
        st.voicing = v;
        for (let k = 0; k < pat.length; k++) {
          const ch = pat[k];
          const mul = ch === 'X' ? 1 : ch === 'x' ? 0.75 : ch === 'o' ? 0.45 : 0;
          if (!mul) continue;
          const vel = baseVel * mul * this.vary();
          for (const n of v) push(k * step, Math.min(0.9, step * 2.2) * (part.legato ?? 1), n, vel);
        }
        return;
      }
      case 'sparkle': {
        const rate = part.rate ?? 0.5;
        const tones = notesInRange(chord.pcs, lo, hi);
        if (!tones.length) return;
        const p = (part.prob ?? 0.1) * inten;
        for (let b = 0; b < beats - 1e-9; b += rate) {
          if (this.rng.next() < p)
            push(b, 1.2, this.rng.pick(tones), baseVel * (0.6 + this.rng.next() * 0.5));
        }
        return;
      }
      case 'drone': {
        if (plan.barInSection % 4 !== 0) return;
        const root = lowestOf(this.tonicPc, lo, hi);
        const fifth = root + 7 <= hi ? root + 7 : root - 5;
        push(0, beats * 4, root, baseVel);
        push(0, beats * 4, fifth, baseVel * 0.7);
        return;
      }
      case 'swell': {
        if (plan.barInSection !== this.sectionBars - 1) return;
        const len = Math.min(2, beats - 0.5);
        push(beats - len, len, 60, baseVel);
        return;
      }
    }
  }

  // ------------------------------------------------------------------ melody

  private scaleSteps(a: number, b: number): number {
    if (a === b) return 0;
    const dir = b > a ? 1 : -1;
    let n = 0;
    let x = a;
    while ((dir > 0 ? x < b : x > b) && Math.abs(n) < 24) {
      x = stepInScale(x, dir, this.tonicPc, this.theme.scale);
      n += dir;
    }
    return n;
  }

  private melody(
    part: PartDef,
    st: PartState,
    plan: BarPlan,
    lo: number,
    hi: number,
    baseVel: number,
    push: (beat: number, dur: number, midi: number, vel: number) => void,
  ): void {
    const beats = plan.beats;
    const chord = plan.chord;
    const phraseBar = plan.barInSection % 4;
    const density = part.density ?? 0.8;
    const allowed = melodyPcs(chord.pcs, this.scale);
    // Resting bars let the arrangement breathe; phrases always start.
    if (phraseBar !== 0) {
      const restP = phraseBar === 2 ? (1 - density) * 0.4 : 1 - density;
      if (this.rng.next() < restP) return;
    }
    const rhythms = this.theme.rhythms ?? DEFAULT_RHYTHMS[this.feel];
    const notes: MelodyNote[] = [];
    const motif =
      phraseBar === 0 &&
      plan.barInSection === 0 &&
      part.motif !== undefined &&
      this.rng.next() < part.motif;

    if (motif) {
      // The leitmotif: chord root, up a fifth, one step higher, held.
      const rhythm = LEITMOTIF.rhythm[this.feel];
      const root = nearestInSet(st.prev, [chord.root], lo, Math.max(lo, hi - 9));
      let b = 0;
      LEITMOTIF.steps.forEach((s, k) => {
        let midi = s === 0 ? root : stepInScale(root, s, this.tonicPc, this.theme.scale);
        if (!allowed.includes(pc(midi))) midi = nearestInSet(midi, allowed, lo, hi);
        notes.push({ beat: b, dur: rhythm[k] ?? 1, midi: fold(midi, lo, hi), strong: k === 0 });
        b += rhythm[k] ?? 1;
      });
      st.call = notes.map((n) => ({ ...n }));
    } else if (phraseBar === 2 && st.call?.length) {
      // Sequence: the call's rhythm and contour, re-harmonised over this chord.
      const call = st.call;
      let prev = nearestInSet(call[0]!.midi, chord.pcs, lo, hi);
      notes.push({ ...call[0]!, midi: prev });
      for (let k = 1; k < call.length; k++) {
        const c = call[k]!;
        let m = stepInScale(
          prev,
          this.scaleSteps(call[k - 1]!.midi, c.midi),
          this.tonicPc,
          this.theme.scale,
        );
        m = fold(m, lo, hi);
        if (c.strong) m = nearestInSet(m, chord.pcs, lo, hi);
        else if (!allowed.includes(pc(m))) m = nearestInSet(m, allowed, lo, hi);
        notes.push({ ...c, midi: m });
        prev = m;
      }
    } else {
      let rhythm: readonly number[];
      if (phraseBar === 3) {
        const cad = rhythms.filter((r) => (r[r.length - 1] ?? 0) >= beats / 2);
        rhythm = this.rng.pick(cad.length ? cad : rhythms);
      } else {
        rhythm = this.rng.pick(rhythms);
      }
      let b = 0;
      let prev = st.prev;
      let lastIv = st.lastInterval;
      rhythm.forEach((d, k) => {
        const strong = k === 0 || this.isStrong(b);
        let midi: number;
        if (k === rhythm.length - 1 && phraseBar === 3) {
          // Cadence: resolve to a chord tone (the tonic at the end of a section).
          const endOfSection =
            plan.barInSection === this.sectionBars - 1 && chord.pcs.includes(this.tonicPc);
          midi = nearestInSet(prev, endOfSection ? [this.tonicPc] : chord.pcs.slice(0, 3), lo, hi);
        } else {
          midi = chooseMelodyNote(this.rng, {
            prev,
            lastInterval: lastIv,
            chordPcs: chord.pcs,
            allowedPcs: allowed,
            lo,
            hi,
            strong,
          });
        }
        notes.push({ beat: b, dur: d, midi, strong });
        lastIv = midi - prev;
        prev = midi;
        b += d;
      });
      if (phraseBar === 0) st.call = notes.map((n) => ({ ...n }));
    }

    const legato = part.legato ?? 0.92;
    for (const n of notes) {
      const v = baseVel * (n.strong ? 1 : 0.84) * this.vary();
      if (part.ornament && n.dur >= 1 && n.beat > 0 && this.rng.next() < part.ornament) {
        const grace = fold(stepInScale(n.midi, 1, this.tonicPc, this.theme.scale), lo, hi);
        push(n.beat, 0.12, grace, v * 0.8);
        push(n.beat + 0.12, n.dur * legato - 0.12, n.midi, v);
      } else {
        push(n.beat, n.dur * legato, n.midi, v);
      }
    }
    const lastN = notes[notes.length - 1];
    if (lastN) {
      const prevN = notes[notes.length - 2];
      if (prevN) st.lastInterval = lastN.midi - prevN.midi;
      st.prev = lastN.midi;
    }
  }
}

/** Parses every chord of every theme (throws on typos); used by tests and dev tools. */
export function validateTheme(theme: ThemeDef): string[] {
  const problems: string[] = [];
  const tonic = pc(theme.tonic);
  for (const p of theme.progressions)
    for (const s of p) {
      try {
        parseRoman(s, tonic);
      } catch (e) {
        problems.push(String(e));
      }
    }
  theme.parts.forEach((p, i) => {
    const needsRange = [
      'chord',
      'arp',
      'bass',
      'melody',
      'ostinato',
      'stab',
      'sparkle',
      'drone',
    ].includes(p.role);
    if (needsRange && !p.range) problems.push(`${theme.id} part ${i} (${p.inst}) needs a range`);
    if (p.range && p.range[1] - p.range[0] < 11)
      problems.push(`${theme.id} part ${i} range narrower than an octave`);
    if ((p.role === 'drums' || p.role === 'stab' || p.role === 'ostinato') && !p.pattern)
      problems.push(`${theme.id} part ${i} needs a pattern`);
  });
  return problems;
}
